import { ValidationError, type BankStatement } from '@app/domain/entities';

type ParsedBankStatement = Omit<BankStatement, 'appAccountId'>;
import {
  isCsvFile,
  isSupportedStatementFile,
  parseCsvFile,
  parseExcelFile,
  type TableCell,
} from './statement-table-reader';
import {
  findHeaderIndex,
  normalizeHeaders,
  resolveAmountFromColumns,
  resolveCurrencyCodeFromColumns,
} from './statement-column-utils';
import { mapStatementRows } from './statement-row-parser';
import { buildExternalId, normalizeCell, normalizeHeader, parseDateValue } from './statement-value-utils';

const SANTANDER_HEADER_ALIASES = {
  date: [
    'date',
    'transaction date',
    'operation date',
    'booking date',
    'value date',
    // Spanish (Santander Spain)
    'fecha',
    'fecha operacion',
    'fecha operación',
    'f operacion',
    'f operación',
    'fecha valor',
    'f valor',
    'fecha mov',
    'fecha movimiento',
    // Polish (Santander Poland)
    'data',
    'data operacji',
    'data transakcji',
    'data ksiegowania',
    'data waluty',
    // Ukrainian / Russian
    'дата',
    'дата операції',
    'дата операции',
    'дата проведення',
    'дата та час операції',
  ],
  description: [
    'description',
    'details',
    'purpose',
    'comment',
    'title',
    'beneficiary',
    // Spanish (Santander Spain)
    'concepto',
    'descripcion',
    'descripción',
    'movimiento',
    'observaciones',
    'motivo',
    // Polish (Santander Poland)
    'opis transakcji',
    'opis operacji',
    'tytul',
    'beneficjent',
    // Ukrainian / Russian
    'назначение',
    'призначення',
    'опис',
    'описание',
    'деталі',
    'детали',
  ],
  amount: [
    'amount',
    'sum',
    'transaction amount',
    // Spanish (Santander Spain)
    'importe',
    'cantidad',
    'importe operacion',
    'importe operación',
    'importe movimiento',
    // Polish (Santander Poland)
    'kwota',
    'kwota transakcji',
    'wartość',
    'wartosc',
    // Ukrainian / Russian
    'сума',
    'сумма',
    'сума операції',
  ],
  debit: [
    'debit',
    'withdrawal',
    'expense',
    // Spanish (Santander Spain)
    'cargo',
    'cargos',
    'debe',
    // Polish (Santander Poland)
    'obciążenie',
    'obciazenie',
    // Ukrainian / Russian
    'дебет',
    'списання',
    'витрата',
    'расход',
  ],
  credit: [
    'credit',
    'deposit',
    'income',
    // Spanish (Santander Spain)
    'abono',
    'abonos',
    'haber',
    // Polish (Santander Poland)
    'uznanie',
    // Ukrainian / Russian
    'кредит',
    'зарахування',
    'зачисление',
    'дохід',
    'доход',
  ],
  currency: [
    'currency',
    // Spanish (Santander Spain)
    'divisa',
    'moneda',
    // Polish (Santander Poland)
    'waluta',
    // Ukrainian / Russian
    'валюта',
    'curr',
  ],
  reference: [
    'reference',
    'reference number',
    'document number',
    'id',
    // Spanish (Santander Spain)
    'referencia',
    'num apunte',
    'num referencia',
    'numero apunte',
    'número apunte',
    'numero de apunte',
    'número de apunte',
    'n referencia',
    'nº referencia',
    // Polish (Santander Poland)
    'numer referencyjny',
    'numer dokumentu',
    // Ukrainian / Russian
    'референс',
    'номер документа',
    'номер документу',
  ],
} as const;

interface HeaderIndexes {
  date: number;
  description: number;
  amount: number | null;
  debit: number | null;
  credit: number | null;
  currency: number | null;
  reference: number | null;
}

export class SantanderFileParserService {
  async parseFile(file: File): Promise<ParsedBankStatement[]> {
    if (!isSupportedStatementFile(file)) {
      throw new ValidationError('file', 'The selected file must be a CSV or Excel statement (.csv, .xlsx)');
    }

    const table = await this.loadTable(file);
    return mapSantanderTableRows(table);
  }

  protected async loadTable(file: File): Promise<TableCell[][]> {
    if (isCsvFile(file)) {
      return parseCsvFile(file);
    }
    return parseExcelFile(file);
  }
}

export function mapSantanderTableRows(rows: TableCell[][]): ParsedBankStatement[] {
  return mapStatementRows(rows, {
    resolveHeaderIndexes,
    mapRow,
    getRowKey: (row) => row.externalId,
  });
}

function resolveHeaderIndexes(headerRow: TableCell[]): HeaderIndexes {
  const headers = normalizeHeaders(headerRow);
  const date = findHeaderIndex(headers, SANTANDER_HEADER_ALIASES.date);
  const description = findHeaderIndex(headers, SANTANDER_HEADER_ALIASES.description);
  const amount = findHeaderIndex(headers, SANTANDER_HEADER_ALIASES.amount);
  const debit = findHeaderIndex(headers, SANTANDER_HEADER_ALIASES.debit);
  const credit = findHeaderIndex(headers, SANTANDER_HEADER_ALIASES.credit);
  const currency = findHeaderIndex(headers, SANTANDER_HEADER_ALIASES.currency);
  const reference = findHeaderIndex(headers, SANTANDER_HEADER_ALIASES.reference);

  if (date === null || description === null || (amount === null && debit === null && credit === null)) {
    const found = headers.filter((h) => h).join(', ') || '(no headers detected)';
    const missing: string[] = [];
    if (date === null) missing.push('date');
    if (description === null) missing.push('description');
    if (amount === null && debit === null && credit === null) missing.push('amount or debit/credit');

    throw new ValidationError(
      'file',
      `Missing required Santander columns: ${missing.join(', ')}. Found headers: ${found}`,
    );
  }

  return {
    date,
    description,
    amount,
    debit,
    credit,
    currency,
    reference,
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function mapRow(row: TableCell[], headerIndexes: HeaderIndexes, _rowNumber: number): ParsedBankStatement | null {
  const description = normalizeCell(row[headerIndexes.description]);
  if (isSummaryRow(description)) {
    return null;
  }

  const date = parseDateValue(row[headerIndexes.date]);
  if (!date) {
    return null;
  }

  const amount = resolveAmountFromColumns(row, headerIndexes);
  if (amount === null || amount === 0) {
    return null;
  }

  const currencyCode = resolveCurrencyCodeFromColumns(row, headerIndexes);
  if (!currencyCode) return null;

  const reference = normalizeCell(headerIndexes.reference === null ? null : row[headerIndexes.reference]) || null;
  const normalizedDescription = description || reference || '';

  return {
    externalId: buildExternalId({
      date,
      amount,
      currencyCode,
      description: normalizedDescription,
      reference,
    }),
    date,
    amount,
    currencyCode,
    description: normalizedDescription,
    reference,
  };
}

function isSummaryRow(description: string): boolean {
  const normalized = normalizeHeader(description);
  return normalized === 'total' || normalized === 'итого' || normalized === 'разом';
}
